/**
 * MHV Bidding Phase — Shared client-side gating + bidding-opens fireworks.
 *
 * Loaded on the auction hub (auction-hub-grid section) and individual lot
 * detail pages (mhv-auction-lot section). Fetches /api/schedule/live-status,
 * sets window.MHV_BIDDING_PHASE and a body class, and runs a 1s ticker that
 * flips the page from "locked" to "live" the moment bidding_open_time hits —
 * dispatching `mhv:bidding-opened` and firing a screen-wide celebration.
 *
 * Phases:
 *   'bidding'    — catalog is launched but bidding_open_time is in the future
 *   'live'       — bidding is open
 *   'done'       — auction has ended
 *   'no-bidding' — absentee bidding is OFF for this auction: the hub is browse +
 *                  sold-results only, no bid UI at any point (absentee is the gate)
 *   'unknown'    — API failed or no event; treat as live (don't block bidding)
 *
 * Reads optional config from window.MHV_BIDDING_PHASE_CONFIG = { sessionCode }.
 */
(function() {
  'use strict';

  var cfg = window.MHV_BIDDING_PHASE_CONFIG || {};
  var sessionCode = cfg.sessionCode || '';

  window.MHV_BIDDING_PHASE = 'unknown';
  // MHV namespace may be created by mhv-utils.js; coalesce so we can stash here too.
  window.MHV = window.MHV || {};

  // The inline blocking script in the section file already set
  // html.mhv-bidding-loading before any bid forms rendered, so both
  // wrappers are display:none from the moment the page paints. We just
  // need to swap that class out once the phase resolves.

  // -----------------------------------------------------------------------
  // Phase resolution: fetch live status, set state, start ticker if needed
  // -----------------------------------------------------------------------
  function setPhase(newPhase) {
    if (window.MHV_BIDDING_PHASE === newPhase) return;
    window.MHV_BIDDING_PHASE = newPhase;
    var root = document.documentElement;
    if (!root) return;
    root.classList.remove('mhv-bidding-loading', 'mhv-bidding-bidding', 'mhv-bidding-live', 'mhv-bidding-done', 'mhv-bidding-no-bidding', 'mhv-bidding-unknown');
    root.classList.add('mhv-bidding-' + newPhase);
  }

  function applyPhase(phase) {
    setPhase(phase);
    if (phase === 'live') {
      document.dispatchEvent(new CustomEvent('mhv:bidding-live'));
    }
  }

  function fetchStatus() {
    var url = MHV.SERVER_URL + '/api/schedule/live-status';
    if (sessionCode) url += '?session=' + encodeURIComponent(sessionCode);
    var f = (window.mhvFetch || window.fetch).bind(window);
    return f(url).then(function(r) { return r.json(); });
  }

  // ── Absentee close notice (tweak 5) ───────────────────────────────────────
  // Server-side enforcement already exists: /place-bid rejects an absentee bid once
  // the deadline passes (lib/bidding-close.js). What was missing was TELLING anyone —
  // the hub simply stopped offering bids.
  //
  // ⚠️ This is ABSENTEE only. LIVE bidding runs over a different path entirely (the
  // place-live-bid socket in the chat widget / live bid bar), which never consults
  // this deadline. Nothing here may ever hide or disable a LIVE bid control.
  var closeNoteEl = null;

  function closeNoteHost() {
    if (closeNoteEl && closeNoteEl.isConnected) return closeNoteEl;
    closeNoteEl = document.getElementById('mhv-absentee-close-note');
    if (closeNoteEl) return closeNoteEl;
    // Self-inject at the top of the hub grid (or the lot page's bid area) so this
    // works on every surface without editing each template.
    // Hub grid first (#product-grid / .filters-container), then the lot page's bid
    // block, then main as a last resort — so the notice lands above the lots on the
    // hub and above the bid form on a lot page.
    var anchor = document.querySelector('#product-grid, .filters-container, .hub-absentee-product-bid, main');
    if (!anchor || !anchor.parentNode) return null;
    closeNoteEl = document.createElement('div');
    closeNoteEl.id = 'mhv-absentee-close-note';
    closeNoteEl.className = 'mhv-absentee-close-note';
    closeNoteEl.setAttribute('aria-live', 'polite');
    anchor.parentNode.insertBefore(closeNoteEl, anchor);
    return closeNoteEl;
  }

  function fmtCountdown(ms) {
    var t = Math.max(0, Math.floor(ms / 1000));
    var d = Math.floor(t / 86400); t -= d * 86400;
    var h = Math.floor(t / 3600);  t -= h * 3600;
    var m = Math.floor(t / 60);    var sec = t - m * 60;
    function p(n) { return (n < 10 ? '0' : '') + n; }
    if (d > 0) return d + (d === 1 ? ' day ' : ' days ') + p(h) + ':' + p(m) + ':' + p(sec);
    return p(h) + ':' + p(m) + ':' + p(sec);
  }

  function renderCloseNotice(closeDate) {
    var el = closeNoteHost();
    if (!el) return;
    var left = closeDate.getTime() - Date.now();
    // Beyond a couple of days a live ticking clock is noise — state the time instead.
    var when = closeDate.toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    });
    if (left > 2 * 86400000) {
      el.innerHTML = '<span class="mhv-acn-label">Absentee bidding closes</span> ' +
        '<span class="mhv-acn-when">' + when + '</span>';
    } else {
      el.innerHTML = '<span class="mhv-acn-label">Absentee bidding closes in</span> ' +
        '<span class="mhv-acn-clock">' + fmtCountdown(left) + '</span> ' +
        '<span class="mhv-acn-when">(' + when + ')</span>';
    }
    el.classList.remove('mhv-acn--closed');
    el.style.display = '';
  }

  function markAbsenteeClosed() {
    var r = document.documentElement;
    // A distinct flag from plain 'no-bidding': an auction that never OFFERED absentee
    // bidding must not be told its absentee bidding has "closed".
    if (r) r.classList.add('mhv-absentee-closed');
    var el = closeNoteHost();
    if (!el) return;
    el.classList.add('mhv-acn--closed');
    el.innerHTML = '<span class="mhv-acn-label">Absentee bidding is closed</span> ' +
      '<span class="mhv-acn-when">Bids are now taken live only.</span>';
    el.style.display = '';
  }

  function start() {
    fetchStatus().then(function(data) {
      // Absentee bidding is the master gate for the bidding MECHANISM. When it's
      // explicitly OFF, the hub is browse + sold-results only — no bid UI at any
      // point (upcoming/live/ended). Fail-open: only gate on an explicit `false`
      // (null/undefined/absent → don't block bidding).
      if (data.absentee_bidding === false) { applyPhase('no-bidding'); return; }

      var ended = !!data.ended;
      var biddingOpenTime = data.bidding_open_time || null;
      if (ended) { applyPhase('done'); return; }

      var biddingDate = biddingOpenTime ? new Date(biddingOpenTime) : null;
      var nowMs = Date.now();

      // Absentee CLOSE (hour_before / at_start / custom). Once the online-absentee
      // deadline passes, the hub becomes browse + sold-results only (bids taken live
      // only) — reuse the 'no-bidding' state. `when_sold` sends no close_time.
      var closeDate = data.absentee_close_time ? new Date(data.absentee_close_time) : null;
      var closeValid = closeDate && !isNaN(closeDate.getTime());
      if (closeValid && closeDate.getTime() <= nowMs) {
        markAbsenteeClosed();
        applyPhase('no-bidding');
        return;
      }

      // Once live, watch for the close instant and flip to browse-only when it hits.
      // Also drives the visible countdown, so the deadline is announced rather than
      // silently arriving — a bidder who had the page open should SEE it close.
      function watchClose() {
        if (!closeValid) return;
        renderCloseNotice(closeDate);
        var civ = setInterval(function() {
          if (Date.now() >= closeDate.getTime()) {
            clearInterval(civ);
            markAbsenteeClosed();
            applyPhase('no-bidding');
          } else {
            renderCloseNotice(closeDate);
          }
        }, 1000);
      }

      if (!biddingDate) {
        // No bidding_open_time set → treat as live (don't block bidding).
        applyPhase('live');
        watchClose();
        return;
      }

      if (biddingDate.getTime() <= nowMs) {
        applyPhase('live');
        watchClose();
        return;
      }

      applyPhase('bidding');

      // 1s ticker watching for the flip
      var interval = setInterval(function() {
        if (Date.now() >= biddingDate.getTime()) {
          clearInterval(interval);
          applyPhase('live');
          watchClose();
          // Re-enable the bidding UI on every lot card in place
          document.dispatchEvent(new CustomEvent('mhv:bidding-opened', {
            detail: { biddingOpenTime: biddingDate.toISOString() }
          }));
          // Big screen celebration
          try {
            if (typeof window.MHV.fireBiddingOpenFireworks === 'function') {
              window.MHV.fireBiddingOpenFireworks();
            }
          } catch (err) {
            console.error('Bidding-open fireworks failed:', err);
          }
        }
      }, 1000);
    }).catch(function(err) {
      console.error('mhv-bidding-phase: failed to fetch live status', err);
      // Fail-open: don't block bidding if the status check breaks.
      applyPhase('unknown');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // -----------------------------------------------------------------------
  // Bidirectional fav-star ↔ locked-pill sync.
  //
  // There are up to two .fav-star elements for the same product on a single
  // page: the corner star (rendered by auction-hub-card.liquid or the lot
  // detail header) and the full-width pill (rendered by auction-hub-bid
  // when bidding is locked). Each existing click handler only updates the
  // element it was attached to, so we mirror state across both:
  //
  //  1. MutationObserver on every .fav-star watches data-faved.
  //  2. When one element's data-faved changes, copy state to every
  //     other .fav-star with the same product-id.
  //  3. The element that *received* the click also triggers a brief pulse
  //     animation on its counterpart, so clicking the pill animates the
  //     corner star and vice-versa.
  //
  // Re-entrancy is avoided by an in-flight flag (mirror writes only fire if
  // the source value differs from the target's current value).
  // -----------------------------------------------------------------------
  var STAR_GOLD = '#FFD700';
  var STAR_STROKE_UNFAVED = '#fff';  // white outline (corner-star original)
  var STAR_FILL_UNFAVED = 'none';    // transparent (corner-star original)
  var STAR_PULSE_MS = 450;

  function setStarVisual(star, faved) {
    var icon = star.querySelector('.star-icon');
    if (icon) {
      // Set SVG presentation attributes to match the original corner-star
      // look (white outline unfaved, gold filled faved). The pill's CSS
      // !important rules in mhv-bidding-phase.css override these for the
      // black-outline/white-interior pill treatment, so corner stars and
      // pill stars each end up with the correct visuals.
      icon.setAttribute('fill', faved ? STAR_GOLD : STAR_FILL_UNFAVED);
      icon.setAttribute('stroke', faved ? STAR_GOLD : STAR_STROKE_UNFAVED);
    }
    star.setAttribute('title', faved ? 'Remove from Favs' : 'Add to Favs');

    // Pill-specific: swap label text + interactive class
    if (star.classList.contains('mhv-bid-locked-pill--fav')) {
      var label = star.querySelector('.mhv-bid-locked-pill__label');
      if (label) label.textContent = faved ? 'Favorited' : 'Add to favorites';
    }
  }

  function pulseStar(star) {
    star.classList.remove('mhv-fav-pulse');
    // Force reflow so re-adding the class restarts the animation
    void star.offsetWidth;
    star.classList.add('mhv-fav-pulse');
    setTimeout(function() { star.classList.remove('mhv-fav-pulse'); }, STAR_PULSE_MS);
  }

  function mirrorFavState(sourceStar) {
    var pid = sourceStar.getAttribute('data-product-id');
    if (!pid) return;
    var faved = sourceStar.getAttribute('data-faved') === 'true';
    var siblings = document.querySelectorAll('.fav-star[data-product-id="' + pid + '"]');
    siblings.forEach(function(sib) {
      if (sib === sourceStar) {
        // Source already got its visual update from the original click
        // handler; just make sure label/title are consistent for pills.
        setStarVisual(sib, faved);
        return;
      }
      // Only write if state actually differs (prevents observer loops).
      if ((sib.getAttribute('data-faved') === 'true') !== faved) {
        sib.setAttribute('data-faved', faved ? 'true' : 'false');
        setStarVisual(sib, faved);
        pulseStar(sib);
      }
    });
  }

  function attachFavObserver() {
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        if (m.type !== 'attributes' || m.attributeName !== 'data-faved') return;
        var star = m.target;
        if (!star || !star.getAttribute) return;
        mirrorFavState(star);
      });
    });
    document.querySelectorAll('.fav-star').forEach(function(star) {
      observer.observe(star, { attributes: true, attributeFilter: ['data-faved'] });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachFavObserver);
  } else {
    attachFavObserver();
  }

  // -----------------------------------------------------------------------
  // Bidding-open fireworks — wilder fork of the chat-widget sold-celebration.
  // 3 sequential bursts, ~200 dots total, 5 launch points, brand colors.
  // Reuses .sold-celebration-container / .sold-celebration-dot CSS so no new
  // stylesheet rules are needed.
  // -----------------------------------------------------------------------
  var COLORS = [
    '#FFD700', '#FFD700', '#FFD700',           // gold (fav color)
    '#FF00C2', '#FF00C2',                       // brand magenta
    '#ffffff', '#ffffff', '#ffffff',            // white
    '#00ccff', '#00ff88', '#ffff00', '#ff6666', '#ff9900'
  ];

  function spawnBurst(originX, originY, dotCount, maxRadius) {
    var container = document.querySelector('.sold-celebration-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'sold-celebration-container';
      document.body.appendChild(container);
    }
    for (var i = 0; i < dotCount; i++) {
      var dot = document.createElement('div');
      dot.className = 'sold-celebration-dot';
      dot.style.backgroundColor = COLORS[Math.floor(Math.random() * COLORS.length)];
      var size = 6 + Math.random() * 14;
      dot.style.width = size + 'px';
      dot.style.height = size + 'px';
      dot.style.left = originX + 'px';
      dot.style.top = originY + 'px';
      var angle = Math.random() * Math.PI * 2;
      var distance = 100 + Math.random() * maxRadius;
      var dx = Math.cos(angle) * distance;
      var dy = Math.sin(angle) * distance - 60; // bias upward
      dot.style.transition = 'transform 1.8s cubic-bezier(.15,.7,.25,1), opacity 1.8s ease-out';
      dot.style.transform = 'translate(0, 0) scale(1)';
      container.appendChild(dot);
      (function(d, x, y) {
        setTimeout(function() {
          d.style.transform = 'translate(' + x + 'px, ' + y + 'px) scale(0.2)';
          d.style.opacity = '0';
        }, 10 + Math.random() * 120);
      })(dot, dx, dy);
    }
    return container;
  }

  window.MHV.fireBiddingOpenFireworks = function() {
    var w = window.innerWidth;
    var h = window.innerHeight;
    var origins = [
      { x: w / 2, y: h / 2 },               // center
      { x: w * 0.2, y: h * 0.3 },           // upper-left
      { x: w * 0.8, y: h * 0.3 },           // upper-right
      { x: w * 0.25, y: h * 0.75 },         // lower-left
      { x: w * 0.75, y: h * 0.75 }          // lower-right
    ];
    var container = null;
    // 3 sequential bursts ~400ms apart
    [0, 400, 800].forEach(function(delay) {
      setTimeout(function() {
        origins.forEach(function(o) {
          container = spawnBurst(o.x, o.y, 14, 380);
        });
      }, delay);
    });
    // Clean up well after the last burst's animation finishes
    setTimeout(function() {
      document.querySelectorAll('.sold-celebration-container').forEach(function(c) {
        if (c.parentNode) c.parentNode.removeChild(c);
      });
    }, 3800);
  };
})();
